import React, { useMemo } from 'react';
import pick from 'lodash/pick';

import AlertWarning from '../../shared/alertWarning.component';
import { BatchReportParameters, ReportParameters, isCustomReport } from '../../shared/types/batchReports';
import { isReportSet } from '../../shared/types/customReports';

const MAX_NUMBER_OF_EXPORTED_TABS = 255;

type TooManyExportedSheetsWarningProps = {
  areTooManyExportedTabs: boolean,
  numberOfExportedTabs: number,
};

const getReportsForExportNumber = (
  batchReportParameters: BatchReportParameters
) => {
  const batchReportsIncludedInExportKeys = Object.keys(batchReportParameters)
    .filter(key => batchReportParameters[key].isIncludedInExport);
  const exportedBatchReports = pick(batchReportParameters, batchReportsIncludedInExportKeys);

  return Object.values(exportedBatchReports).reduce((acc: number, reportParameters: ReportParameters) => {
    const reportDefinition = reportParameters?.reportDefinition;
    if (isCustomReport(reportDefinition) && isReportSet(reportDefinition)) {
      if (reportDefinition.reports) {
        return acc + reportDefinition.reports.length;
      }

      return acc;
    }

    return acc + 1;
  }, 0);
};


export const TooManyExportedTabsWarning = ({
  areTooManyExportedTabs,
  numberOfExportedTabs,
}: TooManyExportedSheetsWarningProps) => {
  if (areTooManyExportedTabs) {
    return <AlertWarning
      warningMessage={`Batch Report export contains ${numberOfExportedTabs} excel tabs.
        The export is limited to ${MAX_NUMBER_OF_EXPORTED_TABS} number of tabs.
        Please reduce number of exported tabs`}
      show={areTooManyExportedTabs}
    />;
  }

  return null;
};

export const useTooManyExportedTabsWarning = (
  batchReportParameters: BatchReportParameters
) => {
  const numberOfExportedTabs = useMemo(() =>
    getReportsForExportNumber(batchReportParameters), [batchReportParameters]);

  const areTooManyExportedTabs = numberOfExportedTabs > MAX_NUMBER_OF_EXPORTED_TABS;

  return {
    areTooManyExportedTabs,
    numberOfExportedTabs,
  };
};
